import { useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import { ApiError } from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { ErrorBanner, InfoBanner } from "../../components/ErrorBanner.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  TextArea,
  TextInput,
} from "../../components/FormField.js";
import { InlineLoading } from "../../components/Spinner.js";
import { PinyinMigrationDialog } from "./PinyinMigrationDialog.js";
import { cn } from "../../lib/cn.js";
import type { OrgDetailContext } from "./context.js";

/**
 * Fix 8 · 催化剂老用户迁移工具（super-admin 一次性触发）。
 *
 * Tab A — 从外部 mongo 导入（POST /migration/dryrun-external-mongo + /execute）
 * Tab B — 拼音绑飞书（复用现有 PinyinMigrationDialog；UsersPanel 也曝露此入口）
 *
 * 一次性工具，amber banner 警示不可幂等（Tab A execute 写库后再跑会 skipped）。
 */

type MigrationTab = "external-mongo" | "pinyin";

interface DryrunCandidate {
  sourceId: string;
  user: string;
  roleCode?: string;
  hasPassPlain: boolean;
  hasPassHash: boolean;
  conflictsWithExisting: boolean;
}

interface DryrunResponse {
  candidates: DryrunCandidate[];
  totalScanned: number;
  normalizedCount: number;
  limit: number;
}

interface ExecuteResponse {
  created: Array<{ user: string; id: string; lazyMigrate: boolean }>;
  skipped: Array<{ user: string; reason: string }>;
  failed: Array<{ user: string; error: string }>;
  totalScanned: number;
}

export function MigrationPanel(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const [tab, setTab] = useState<MigrationTab>("external-mongo");
  const [pinyinOpen, setPinyinOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h2 className="text-base font-semibold text-dosia-text">迁移工具</h2>
        <p className="text-xs text-dosia-text-secondary">
          一次性工具：把催化剂老库用户搬到 admin-portal，按拼音 / 邮箱前缀 / 手机
          号自动绑飞书。
        </p>
      </div>

      <div className="flex border-b border-dosia-border">
        {(
          [
            ["external-mongo", "外部 mongo 导入"],
            ["pinyin", "拼音绑飞书"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              "px-3 py-1.5 text-sm border-b-2 -mb-px",
              tab === key
                ? "border-dosia-accent text-dosia-text"
                : "border-transparent text-dosia-text-secondary hover:text-dosia-text",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "external-mongo" ? <ExternalMongoTab orgCode={org.code} /> : null}

      {tab === "pinyin" ? (
        <div className="flex flex-col gap-3">
          <InfoBanner>
            <span className="text-xs">
              拼音绑飞书复用 POST /api/organizations/:orgCode/users/pinyin-migration
              端点：从 admin-portal users 中拉未绑飞书的 user → 按 pinyin / email
              prefix / mobile exact 匹配飞书 roster → 弹 review 弹窗 → 一键批量绑定。
            </span>
          </InfoBanner>
          <div>
            <PrimaryButton onClick={() => setPinyinOpen(true)}>
              打开拼音匹配工具
            </PrimaryButton>
          </div>
          <PinyinMigrationDialog
            orgCode={org.code}
            open={pinyinOpen}
            onClose={() => setPinyinOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

function ExternalMongoTab({ orgCode }: { orgCode: string }): JSX.Element {
  const auth = useAtomValue(authAtom);
  const [mongoUri, setMongoUri] = useState("");
  const [dbName, setDbName] = useState("");
  const [collection, setCollection] = useState("users");
  const [roleMappingText, setRoleMappingText] = useState("");
  const [defaultRoleCode, setDefaultRoleCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dryrun, setDryrun] = useState<DryrunResponse | null>(null);
  const [result, setResult] = useState<ExecuteResponse | null>(null);

  const parseRoleMapping = (): Record<string, string> | null => {
    if (!roleMappingText.trim()) return {};
    try {
      const parsed = JSON.parse(roleMappingText) as Record<string, string>;
      // 仅接受 string -> string 的映射
      for (const v of Object.values(parsed)) {
        if (typeof v !== "string") return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  const callEndpoint = async <T,>(suffix: "dryrun-external-mongo" | "execute"): Promise<T | null> => {
    if (!auth) return null;
    setError(null);
    setLoading(true);
    try {
      const roleMapping = suffix === "execute" ? parseRoleMapping() : undefined;
      if (suffix === "execute" && roleMappingText.trim() && roleMapping === null) {
        throw new Error("roleMapping JSON 解析失败（必须是 { 旧 code: 新 code } string→string）");
      }
      return await apiCall<T>(
        "POST",
        `/api/organizations/${orgCode}/migration/${suffix}`,
        {
          token: auth.token,
          body: {
            mongoUri,
            dbName: dbName || undefined,
            collection,
            ...(suffix === "execute"
              ? {
                  roleMapping,
                  defaultRoleCode: defaultRoleCode || undefined,
                }
              : {}),
          },
        },
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleDryrun = async (): Promise<void> => {
    setResult(null);
    const res = await callEndpoint<DryrunResponse>("dryrun-external-mongo");
    if (res) setDryrun(res);
  };

  const handleExecute = async (): Promise<void> => {
    const res = await callEndpoint<ExecuteResponse>("execute");
    if (res) {
      setResult(res);
      setDryrun(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded border border-amber-500/30 bg-amber-500/15 px-3 py-2 text-xs text-amber-500">
        一次性操作：执行后会在 admin-portal.users 创建新行。重复跑会按 user.username
        幂等（同名 active/pending → skipped）；建议先「探测」review 后再「执行」。
      </div>

      {error ? <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner> : null}

      <div className="grid grid-cols-2 gap-3">
        <FormField label="mongoUri" required hint="老 localcode 的 mongo 连接串">
          <TextInput
            value={mongoUri}
            onChange={(e) => setMongoUri(e.target.value)}
            placeholder="mongodb://user:pass@host:27017/db"
          />
        </FormField>
        <FormField label="dbName" hint="不填 = 用 URI 里的 db">
          <TextInput
            value={dbName}
            onChange={(e) => setDbName(e.target.value)}
          />
        </FormField>
        <FormField label="collection" required>
          <TextInput
            value={collection}
            onChange={(e) => setCollection(e.target.value)}
            placeholder="users"
          />
        </FormField>
        <FormField label="默认 role code" hint="无 roleCode 字段的 row 兜底">
          <TextInput
            value={defaultRoleCode}
            onChange={(e) => setDefaultRoleCode(e.target.value)}
            placeholder="employee"
          />
        </FormField>
      </div>

      <FormField
        label="roleMapping (JSON, 仅执行时用)"
        hint='{ "旧 code": "新 code" } string → string'
      >
        <TextArea
          rows={3}
          value={roleMappingText}
          onChange={(e) => setRoleMappingText(e.target.value)}
          placeholder='{ "media-old": "media", "ae-old": "client" }'
        />
      </FormField>

      <div className="flex gap-2">
        <SecondaryButton
          onClick={() => void handleDryrun()}
          disabled={!mongoUri || !collection || loading}
        >
          探测（dry-run）
        </SecondaryButton>
        <PrimaryButton
          onClick={() => void handleExecute()}
          disabled={!mongoUri || !collection || loading}
        >
          执行（落库）
        </PrimaryButton>
      </div>

      {loading ? <InlineLoading /> : null}

      {dryrun ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-dosia-text">
            扫到 <strong>{dryrun.totalScanned}</strong> 条，可识别{" "}
            <strong>{dryrun.normalizedCount}</strong> 条（limit {dryrun.limit}）。
          </p>
          <div className="border border-dosia-border rounded-md max-h-[320px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-dosia-bg-lighter sticky top-0">
                <tr>
                  <th className="text-left p-2">user</th>
                  <th className="text-left p-2">roleCode</th>
                  <th className="text-left p-2">密码</th>
                  <th className="text-left p-2">冲突？</th>
                  <th className="text-left p-2">sourceId</th>
                </tr>
              </thead>
              <tbody>
                {dryrun.candidates.map((c) => (
                  <tr
                    key={c.sourceId}
                    className={c.conflictsWithExisting ? "text-amber-500" : ""}
                  >
                    <td className="p-2 font-mono">{c.user}</td>
                    <td className="p-2">{c.roleCode ?? "—"}</td>
                    <td className="p-2">
                      {c.hasPassPlain ? "明文" : c.hasPassHash ? "哈希" : "无"}
                    </td>
                    <td className="p-2">
                      {c.conflictsWithExisting ? "⚠ 同名" : "OK"}
                    </td>
                    <td className="p-2 font-mono text-dosia-text-dim">
                      {c.sourceId}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {result ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-dosia-text">
            扫到 <strong>{result.totalScanned}</strong> 条 — 创建{" "}
            <strong>{result.created.length}</strong> · 跳过{" "}
            <strong>{result.skipped.length}</strong> · 失败{" "}
            <strong>{result.failed.length}</strong>
          </p>
          {result.failed.length > 0 ? (
            <details open>
              <summary className="cursor-pointer text-amber-500">
                失败明细
              </summary>
              <ul className="text-xs mt-2 space-y-1">
                {result.failed.map((f, i) => (
                  <li key={i} className="text-amber-500">
                    {f.user} — {f.error}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
